import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  Avatar,
  Divider,
  Chip
} from '@mui/material';
import { 
  Person,
  Logout, 
  Security,
  AccessTime,
  AccountCircle
} from '@mui/icons-material';
import { authService } from '../services/authService';

interface ProfilePageProps {
  fontFamily?: string;
}

export const ProfilePage: React.FC<ProfilePageProps> = ({ fontFamily = 'JetBrains Mono' }) => {
  const currentUser = authService.getCurrentUser();
  const isAuthEnabled = authService.isAuthEnabled();

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      authService.logout();
      window.location.reload(); // This will trigger the app to show login page
    }
  };

  if (!isAuthEnabled) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h4" gutterBottom sx={{ fontFamily }}>
          👤 Profile
        </Typography>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontFamily, color: 'text.secondary' }}>
            Authentication is disabled
          </Typography>
          <Typography variant="body2" sx={{ fontFamily, mt: 1 }}>
            No user profile available when authentication is turned off.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom sx={{ fontFamily, mb: 3 }}>
        👤 User Profile
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3 }}>
        {/* Main Profile Card */}
        <Box>
          <Card elevation={3} sx={{ height: '100%' }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar 
                  sx={{ 
                    width: 80, 
                    height: 80, 
                    bgcolor: 'primary.main',
                    fontSize: '2rem',
                    mr: 3
                  }}
                >
                  <AccountCircle sx={{ fontSize: '3rem' }} />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontFamily, fontWeight: 'bold' }}>
                    {currentUser}
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily, color: 'text.secondary', mt: 0.5 }}>
                    Dashboard User
                  </Typography>
                  <Chip 
                    icon={<Security />}
                    label="Authenticated"
                    color="success"
                    size="small"
                    sx={{ mt: 1, fontFamily }}
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" sx={{ fontFamily, mb: 2 }}>
                📋 Account Information
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontFamily, fontWeight: 'bold', mb: 0.5 }}>
                  Username:
                </Typography>
                <Typography variant="body1" sx={{ fontFamily, color: 'text.secondary' }}>
                  {currentUser}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontFamily, fontWeight: 'bold', mb: 0.5 }}>
                  Authentication Status:
                </Typography>
                <Typography variant="body1" sx={{ fontFamily, color: 'success.main' }}>
                  ✅ Logged In
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontFamily, fontWeight: 'bold', mb: 0.5 }}>
                  Session Type:
                </Typography>
                <Typography variant="body1" sx={{ fontFamily, color: 'text.secondary' }}>
                  Local Storage (24 hours)
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" sx={{ fontFamily, mb: 2 }}>
                🔐 Security Settings
              </Typography>
              
              <Paper elevation={1} sx={{ p: 2, backgroundColor: '#f8f9fa', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AccessTime sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="body2" sx={{ fontFamily, fontWeight: 'bold' }}>
                    Session Management
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ fontFamily, color: 'text.secondary' }}>
                  Your session will automatically expire after 24 hours for security purposes.
                </Typography>
              </Paper>

              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<Logout />}
                  onClick={handleLogout}
                  sx={{ fontFamily }}
                >
                  Logout
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => window.location.reload()}
                  sx={{ fontFamily }}
                >
                  Refresh Session
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Quick Actions Card */}
        <Box>
          <Card elevation={3} sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontFamily, mb: 2 }}>
                ⚡ Quick Actions
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<Person />}
                  sx={{ fontFamily, mb: 2 }}
                  onClick={() => alert('Profile settings coming soon!')}
                >
                  Edit Profile
                </Button>
                
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<Security />}
                  sx={{ fontFamily, mb: 2 }}
                  onClick={() => alert('Security settings coming soon!')}
                >
                  Security Settings
                </Button>

                <Button
                  variant="outlined"
                  color="error"
                  fullWidth
                  startIcon={<Logout />}
                  sx={{ fontFamily }}
                  onClick={handleLogout}
                >
                  Sign Out
                </Button>
              </Box>

              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" sx={{ fontFamily, mb: 1 }}>
                📊 Dashboard Access
              </Typography>
              <Typography variant="body2" sx={{ fontFamily, color: 'text.secondary', mb: 2 }}>
                You have access to all available dashboards and analytics tools.
              </Typography>
              
              <Chip 
                label="Full Access"
                color="primary"
                size="small"
                sx={{ fontFamily }}
              />
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
}; 